import React, { useEffect } from 'react';
import { Card, CardContent, Box, Typography, IconButton, FormControl, InputLabel, Select, MenuItem, Button, TextField, Paper } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';

const QuestionnaireBlock = ({ block, index, availableScales, updateBlock, removeBlock }) => {
    console.log('Rendering QuestionnaireBlock with block:', block);

    useEffect(() => {
        console.log('QuestionnaireBlock received updated block:', block);
    }, [block]);

    const addQuestion = () => {
        const updatedQuestions = [...(block.questions || []), {
            type: 'likert',
            text: '',
            options: ['1', '2', '3', '4', '5', '6', '7'],
            required: true
        }];
        updateBlock(index, { ...block, questions: updatedQuestions });
    };

    const updateQuestion = (questionIndex, updatedQuestion) => {
        const updatedQuestions = [...block.questions];
        updatedQuestions[questionIndex] = updatedQuestion;
        updateBlock(index, { ...block, questions: updatedQuestions });
    };

    const removeQuestion = (questionIndex) => {
        const updatedQuestions = block.questions.filter((_, i) => i !== questionIndex);
        updateBlock(index, { ...block, questions: updatedQuestions });
    };

    // Change scale matching to use unique _id
    const handleScaleSelection = (scaleId) => {
        const selectedScale = availableScales.find(scale => scale._id === scaleId);
        if (selectedScale) {
            console.log('Selected scale:', selectedScale);

            const scaleQuestions = Object.entries(selectedScale.items).map(([item_index, item]) => {
                let options = [];
                if (selectedScale.response_model.range) {
                    const [min, max] = selectedScale.response_model.range;
                    options = Array.from({ length: max - min + 1 }, (_, i) => (min + i).toString());
                } else {
                    options = selectedScale.response_model || ['1', '2', '3', '4', '5', '6', '7'];
                }
                return {
                    type: selectedScale.response_model.type,
                    text: item.text,
                    options: options,
                    required: true,
                };
            });

            updateBlock(index, {
                ...block,
                type: 'scale',
                title: selectedScale.name,
                scale: { ...selectedScale, title: selectedScale.name },
                questions: scaleQuestions,
                randomize: false,
            });
        }
    };

    const uploadScale = (event) => {
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const scaleData = JSON.parse(e.target.result);
                    updateBlock(index, { ...block, scale: scaleData });
                } catch (error) {
                    console.error('Invalid JSON file:', error);
                }
            };
            reader.readAsText(file);
        }
    };

    return (
        <Card sx={{ mb: 2, p: 2 }}>
            <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6">{block.scale ? 'Scale Block' : 'Questionnaire Block'}</Typography>
                    <IconButton onClick={() => removeBlock(index)}>
                        <DeleteIcon />
                    </IconButton>
                </Box>

                {block.scale ? (
                    <>
                        <Typography variant="subtitle1" sx={{ mb: 2 }}>Select or Upload Scale</Typography>
                        <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel>Available Scales</InputLabel>
                            <Select
                                value={block.scale?._id || ''}
                                onChange={(e) => handleScaleSelection(e.target.value)}
                            >
                                {console.log('Available scales:', availableScales)}
                                {availableScales.map((scale) => (
                                    <MenuItem key={scale._id} value={scale._id}>
                                        {scale.name} ({scale.version && `v${scale.version}`}) ({scale.created_at && new Date(scale.created_at).toLocaleDateString()})
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Button
                            variant="outlined"
                            component="label"
                            sx={{ mb: 2 }}
                        >
                            Upload Scale JSON
                            <input
                                type="file"
                                accept="application/json"
                                hidden
                                onChange={uploadScale}
                            />
                        </Button>
                        <TextField
                            fullWidth
                            label="Scale Name"
                            value={block.scale?.title ?? block.scale?.name ?? ''}
                            onChange={(e) => updateBlock(index, { ...block, title: e.target.value, scale: { ...block.scale, title: e.target.value } })}
                            sx={{ mb: 2 }}
                        />
                        {/* Block-level introduction and outroduction */}
                        <Box sx={{ mb: 2 }}>
                            <TextField
                                fullWidth
                                label="Introduction Text"
                                multiline
                                rows={2}
                                value={block.introText || block.introduction_text || ''}
                                onChange={e => updateBlock(index, { ...block, introText: e.target.value })}
                                sx={{ mb: 2 }}
                            />
                            <TextField
                                fullWidth
                                label="Outroduction Text"
                                multiline
                                rows={2}
                                value={block.outroText || block.outroduction_text || ''}
                                onChange={e => updateBlock(index, { ...block, outroText: e.target.value })}
                            />
                        </Box>
                        <Typography variant="subtitle1">Scale Items</Typography>
                        <Box sx={{ maxHeight: '300px', overflow: 'auto', mb: 2 }}>
                            {block.questions.map((item, idx) => {
                                const formatItem = (item) => {
                                    return block.scale.prompt_templates.item_format
                                        .replace('{item_id}', idx + 1)
                                        .replace('{text}', item.text);
                                }
                                return (
                                    <Typography key={idx} variant="body2" sx={{ mb: 1 }}>
                                        {formatItem(item)}
                                    </Typography>
                                )
                            }
                            )}
                        </Box>
                    </>
                ) : (
                    <>
                        <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel>Available Scales</InputLabel>
                            <Select
                                value={block.scale?._id || ''}
                                onChange={(e) => handleScaleSelection(e.target.value)}
                            >
                                {availableScales.map((scale) => (
                                    <MenuItem key={scale._id} value={scale._id}>
                                        {scale.name} ({scale.version && `v${scale.version}`}) ({scale.created_at && new Date(scale.created_at).toLocaleDateString()})
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            fullWidth
                            label="Questionnaire Title"
                            value={block.title || ''}
                            onChange={(e) => updateBlock(index, { ...block, title: e.target.value })}
                            sx={{ mb: 2 }}
                        />

                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Typography variant="subtitle1" sx={{ mr: 2 }}>Randomize Questions:</Typography>
                            <FormControl>
                                <Select
                                    value={block.randomize ? 'true' : 'false'}
                                    onChange={(e) => updateBlock(index, {
                                        ...block,
                                        randomize: e.target.value === 'true'
                                    })}
                                    size="small"
                                >
                                    <MenuItem value="true">Yes</MenuItem>
                                    <MenuItem value="false">No</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                        {/* Block-level introduction and outroduction */}
                        <Box sx={{ mb: 2 }}>
                            <TextField
                                fullWidth
                                label="Introduction Text"
                                multiline
                                rows={2}
                                value={block.introText || block.introduction_text || ''}
                                onChange={e => updateBlock(index, { ...block, introText: e.target.value })}
                                sx={{ mb: 2 }}
                            />
                            <TextField
                                fullWidth
                                label="Outroduction Text"
                                multiline
                                rows={2}
                                value={block.outroText || block.outroduction_text || ''}
                                onChange={e => updateBlock(index, { ...block, outroText: e.target.value })}
                            />
                        </Box>
                        <Typography variant="subtitle1" sx={{ mb: 1 }}>
                            Questions ({(block.questions || []).length} trials)
                        </Typography>
                        <Box sx={{ mb: 2, maxHeight: '300px', overflow: 'auto' }}>
                            {(block.questions || []).map((question, questionIndex) => (
                                <Paper key={questionIndex} sx={{ p: 2, mb: 2, bgcolor: '#f5f5f5' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                        <Typography variant="subtitle2">Question {questionIndex + 1}</Typography>
                                        <IconButton size="small" onClick={() => removeQuestion(questionIndex)}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                    <FormControl fullWidth sx={{ mb: 2 }}>
                                        <InputLabel>Question Type</InputLabel>
                                        <Select
                                            value={question.type || 'likert'}
                                            onChange={(e) => {
                                                const newType = e.target.value;
                                                let updatedQuestion = { ...question, type: newType };
                                                if (newType === 'likert') {
                                                    updatedQuestion.options = ['1', '2', '3', '4', '5', '6', '7'];
                                                } else if (newType === 'multiple_choice') {
                                                    updatedQuestion.options = ['Option 1', 'Option 2', 'Option 3'];
                                                } else if (newType === 'boolean') {
                                                    updatedQuestion.options = ['Yes', 'No'];
                                                } else {
                                                    delete updatedQuestion.options;
                                                }
                                                updateQuestion(questionIndex, updatedQuestion);
                                            }}
                                        >
                                            <MenuItem value="likert">Likert Scale</MenuItem>
                                            <MenuItem value="text">Free Text</MenuItem>
                                            <MenuItem value="boolean">Yes/No</MenuItem>
                                            <MenuItem value="multiple_choice">Multiple Choice</MenuItem>
                                        </Select>
                                    </FormControl>
                                    <TextField
                                        fullWidth
                                        label="Question Text"
                                        multiline
                                        rows={2}
                                        value={question.text || ''}
                                        onChange={(e) => updateQuestion(questionIndex, {
                                            ...question,
                                            text: e.target.value
                                        })}
                                        sx={{ mb: 2 }}
                                    />
                                    {['likert', 'multiple_choice', 'boolean'].includes(question.type) && (
                                        <Box sx={{ mb: 2 }}>
                                            <Typography variant="subtitle2" sx={{ mb: 1 }}>Options:</Typography>
                                            {(question.options || []).map((option, optionIndex) => (
                                                <Box key={optionIndex} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                    <TextField
                                                        size="small"
                                                        value={option}
                                                        onChange={(e) => {
                                                            const updatedOptions = [...question.options];
                                                            updatedOptions[optionIndex] = e.target.value;
                                                            updateQuestion(questionIndex, {
                                                                ...question,
                                                                options: updatedOptions
                                                            });
                                                        }}
                                                        sx={{ mr: 1, width: '120px' }}
                                                    />
                                                    <IconButton size="small" onClick={() => {
                                                        const updatedOptions = question.options.filter((_, i) => i !== optionIndex);
                                                        updateQuestion(questionIndex, {
                                                            ...question,
                                                            options: updatedOptions
                                                        });
                                                    }}>
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            ))}
                                            {['multiple_choice', 'likert'].includes(question.type) && (
                                                <Button
                                                    size="small"
                                                    onClick={() => {
                                                        const updatedOptions = [...(question.options || []), ''];
                                                        updateQuestion(questionIndex, {
                                                            ...question,
                                                            options: updatedOptions
                                                        });
                                                    }}
                                                >
                                                    + Add Option
                                                </Button>
                                            )}
                                        </Box>
                                    )}
                                    <FormControl sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                                        <Typography variant="body2" sx={{ mr: 1 }}>Required:</Typography>
                                        <Select
                                            size="small"
                                            value={question.required ? 'true' : 'false'}
                                            onChange={(e) => updateQuestion(questionIndex, {
                                                ...question,
                                                required: e.target.value === 'true'
                                            })}
                                        >
                                            <MenuItem value="true">Yes</MenuItem>
                                            <MenuItem value="false">No</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Paper>
                            ))}
                        </Box>
                        <Button
                            variant="outlined"
                            onClick={addQuestion}
                            startIcon={<span>+</span>}
                        >
                            Add Question Trial
                        </Button>

                    </>
                )}
            </CardContent>
        </Card>
    );
};

export default QuestionnaireBlock;
